import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Shield, AlertTriangle } from 'lucide-react';

interface MfaResetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  userName: string;
  onSuccess?: () => void;
}

const MfaResetModal: React.FC<MfaResetModalProps> = ({
  open,
  onOpenChange,
  userEmail,
  userName,
  onSuccess
}) => {
  const { toast } = useToast();
  const [isResetting, setIsResetting] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');

  const handleReset = async () => {
    if (confirmEmail !== userEmail) {
      toast({
        title: 'Email Confirmation Required',
        description: 'Please type the user email exactly to confirm the reset',
        variant: 'destructive',
      });
      return;
    }

    if (!resetReason.trim()) {
      toast({
        title: 'Reason Required',
        description: 'Please provide a reason for resetting MFA',
        variant: 'destructive',
      });
      return;
    }

    setIsResetting(true);

    try {
      const { data, error } = await invokeFunction(supabase, 'admin-reset-mfa', {
        body: {
          targetUserEmail: userEmail,
          resetReason: resetReason.trim()
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to reset MFA');
      }

      toast({
        title: 'MFA reset successfully',
        description: `Reset email sent to ${userEmail}.`,
      });

      onOpenChange(false);
      setResetReason('');
      setConfirmEmail('');
      onSuccess?.();

    } catch (error: any) {
      console.error('Error resetting MFA:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset MFA',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setResetReason('');
    setConfirmEmail('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Reset MFA for {userName}
          </DialogTitle>
          <DialogDescription>
            This action will deactivate the current MFA setup for this user and send them a reset link.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important Security Notice</p>
                <p className="mt-1">
                  This action will immediately disable the user's current 2FA setup. 
                  They will receive an email with instructions to set up new MFA.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reset-reason">Reason for Reset *</Label>
            <Textarea
              id="reset-reason"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="e.g., User lost access to phone, device was stolen, etc."
              className="min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-email">
              Confirm User Email *
            </Label>
            <Input
              id="confirm-email"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={`Type ${userEmail} to confirm`}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Type the user's email address exactly to confirm this action
            </p>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
            disabled={isResetting}
          >
            Cancel
          </Button>
          <Button 
            data-testid="confirm-mfa-reset"
            onClick={handleReset}
            disabled={isResetting || confirmEmail !== userEmail || !resetReason.trim()}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {isResetting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting MFA...
              </>
            ) : (
              'Reset MFA'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MfaResetModal;